import type { Dispatch } from "@reduxjs/toolkit";
import { applyPaycallCallTaken } from "../slices/callCenter.slice";
import { mapPaycallCallTakenPayload } from "./mapPaycallPayloads";
import { paycallSoftphone } from "./paycallSoftphone.service";

/**
 * True when this browser is actually registered with the SIP provider.
 *
 * The backend broadcasts PaycallCallStart to every connected agent that has an
 * active AgentSipCredentials row — it has no way of knowing whether the browser
 * managed to REGISTER. When the stored password (or domain / port / WS URL) is
 * wrong, the provider never routes the INVITE here, so the phone never rings and
 * Answer cannot work. Showing the incoming-call UI in that state offers a call the
 * therapist is physically unable to take, so callers get left waiting.
 *
 * Fails OPEN: a bug in this check must never swallow a real call. Silently dropping
 * every incoming call is far worse than showing one that turns out unanswerable.
 */
export function isSipReadyForInboundCalls(): boolean {
  try {
    // Deliberately the SIP stack's own registration state, NOT the derived connection
    // status — the latter can read "error" while the registrar still has a live binding.
    const ready = paycallSoftphone.isSipRegistered();

    if (!ready) {
      console.warn(
        "[Paycall] Incoming call suppressed: softphone is not registered.",
        {
          sipStatus: paycallSoftphone.getSipStatus(),
          extension: paycallSoftphone.getMyExtension(),
        }
      );
    }

    return ready;
  } catch (error) {
    console.error(
      "[Paycall] SIP readiness check failed; showing the call anyway.",
      error
    );
    return true;
  }
}

/** SignalR PaycallCallTaken — normalize payload and update call-center state. */
export function handlePaycallCallTaken(
  dispatch: Dispatch,
  payload: unknown
): void {
  const mapped = mapPaycallCallTakenPayload(
    payload,
    paycallSoftphone.getMyExtension()
  );
  if (!mapped) return;

  // Inconclusive "taken" (no agent id / extension not ready) — keep ringing UI;
  // App.setCallActive runs after the therapist answers locally.
  if (!mapped.answeredByMe && !mapped.hasAnsweredAgent) {
    return;
  }

  dispatch(
    applyPaycallCallTaken({
      callId: mapped.callId,
      answeredByMe: mapped.answeredByMe,
    })
  );
}
